import React from "react";
import { Table, TableBody } from "../../../../components/ui/common/table";
import EmptyRow from "../shared/components/EmptyRow";
import ProcessEditRow from "./components/ProcessEditRow";
import ProcessExpandedRow from "./components/ProcessExpandedRow";
import ProcessReadRow from "./components/ProcessReadRow";
import ProcessTableHeader from "./components/ProcessTableHeader";
import { useProcessRecordCrud } from "./hooks/useProcessRecordCrud";
import { useProcessRecordOptions } from "./hooks/useProcessRecordOptions";
import { useProcessRowEditing } from "./hooks/useProcessRowEditing";
import { ApiProcessingRecord, GateKind } from "./types/processRecords.types";

interface ProcessRecordsListProps {
  processes: ApiProcessingRecord[];
  processGateId: string;
  gateKind: GateKind;
  ownerId: string;
  sourceId: string;
  onUpdate: (next: ApiProcessingRecord[]) => void;
  disabled?: boolean;
}

/**
 * Process table for a gate: read rows, an inline editor, and a lazily-loaded
 * detail panel per expanded row. Composition only — state, persistence and
 * static-data lookups each live in their own hook.
 */
const ProcessRecordsList: React.FC<ProcessRecordsListProps> = ({
  processes,
  processGateId,
  gateKind,
  ownerId,
  sourceId,
  onUpdate,
  disabled = false
}) => {
  const lookups = useProcessRecordOptions();
  const { editingId, isEditing, isExpanded, startEdit, cancelEdit, finishEdit, toggleExpand } =
    useProcessRowEditing();

  const { saveProcess, saveCipSip } = useProcessRecordCrud({
    processes,
    gateKind,
    processGateId,
    ownerId,
    sourceId,
    onUpdate,
    onSaved: finishEdit
  });

  if (processes.length === 0) return <EmptyRow label="processes" />;

  return (
    <div className="tw-overflow-x-auto -tw-mx-3">
      <Table className="tw-text-xs">
        <ProcessTableHeader editing={!!editingId} />
        <TableBody>
          {processes.map(process => {
            const expanded = isExpanded(process._id);

            if (isEditing(process._id)) {
              return (
                <ProcessEditRow
                  key={process._id}
                  process={process}
                  expanded={expanded}
                  gateKind={gateKind}
                  lookups={lookups}
                  onToggleExpand={() => toggleExpand(process._id)}
                  onSave={updated => saveProcess(process._id, updated)}
                  onCancel={cancelEdit}
                  onSaveCipSip={updated => saveCipSip(process._id, updated)}
                />
              );
            }

            return (
              <React.Fragment key={process._id}>
                <ProcessReadRow
                  process={process}
                  expanded={expanded}
                  lookups={lookups}
                  onToggleExpand={() => toggleExpand(process._id)}
                  onEdit={() => !disabled && startEdit(process._id)}
                  disabled={disabled}
                />
                {expanded && (
                  <ProcessExpandedRow
                    process={process}
                    gateKind={gateKind}
                    onSaveCipSip={updated => saveCipSip(process._id, updated)}
                    disabled={disabled}
                  />
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default ProcessRecordsList;
